import { Component, HostListener, ViewEncapsulation } from '@angular/core';
import { IFilterAngularComp } from 'ag-grid-angular';
import { IDoesFilterPassParams, IFilterParams } from 'ag-grid-community';

type AgeRangeKey = '0_1' | '2_5' | '6_15' | '16_plus' | null;
type OperatorKey =
  | 'equals'
  | 'notEqual'
  | 'before'
  | 'after'
  | 'blank'
  | 'notBlank';

@Component({
  selector: 'app-age-range-filter',
  templateUrl: './age-range-filter.component.html',
  styleUrls: ['./age-range-filter.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AgeRangeFilterComponent implements IFilterAngularComp {
  private params!: IFilterParams;

  operator: OperatorKey = 'equals';
  selectedRange: AgeRangeKey = null;

  operatorDropdownOpen = false;
  rangeDropdownOpen = false;

  operatorOptions = [
    { value: 'equals' as OperatorKey, label: 'Equals' },
    { value: 'notEqual' as OperatorKey, label: 'Does not equal' },
    { value: 'before' as OperatorKey, label: 'Before' },
    { value: 'after' as OperatorKey, label: 'After' },
    { value: 'blank' as OperatorKey, label: 'Blank' },
    { value: 'notBlank' as OperatorKey, label: 'Not blank' }
  ];

  rangeOptions = [
    { value: '0_1' as AgeRangeKey, label: '0-1 days' },
    { value: '2_5' as AgeRangeKey, label: '2-5 days' },
    { value: '6_15' as AgeRangeKey, label: '6-15 days' },
    { value: '16_plus' as AgeRangeKey, label: '16+ days' }
  ];

  private readonly rangeOrder: AgeRangeKey[] = [
    '0_1',
    '2_5',
    '6_15',
    '16_plus'
  ];

  agInit(params: IFilterParams): void {
    this.params = params;
  }

  /* === API === */

  isFilterActive(): boolean {
    if (this.operator === 'blank' || this.operator === 'notBlank') {
      return true;
    }
    return this.selectedRange !== null;
  }

  doesFilterPass(p: IDoesFilterPassParams): boolean {
    const field = this.params.colDef.field;
    const raw = field ? p.data[field] : null;
    // Blank / Not blank first
    if (this.operator === 'blank') {
      return raw == null || raw === '';
    }
    if (this.operator === 'notBlank') {
      return !(raw == null || raw === '');
    }

    if (raw == null) {
      return false;
    }

    const age = Number(raw);
    if (Number.isNaN(age)) {
      return false;
    }

    const bucket = this.ageToBucket(age);
    if (!bucket) {
      return false;
    }

    // If no range selected for Equals / Before / After / NotEqual, treat as no filter
    if (!this.selectedRange) {
      return true;
    }

    if (this.operator === 'equals') {
      return bucket === this.selectedRange;
    }

    if (this.operator === 'notEqual') {
      return bucket !== this.selectedRange;
    }

    const idxBucket = this.rangeOrder.indexOf(bucket);
    const idxSelected = this.rangeOrder.indexOf(this.selectedRange);

    if (idxBucket === -1 || idxSelected === -1) {
      return false;
    }

    if (this.operator === 'before') {
      return idxBucket < idxSelected;
    }

    if (this.operator === 'after') {
      return idxBucket > idxSelected;
    }

    return true;
  }

  getModel() {
    return {
      operator: this.operator,
      range: this.selectedRange
    };
  }

  setModel(model: any): void {
    if (model) {
      this.operator = (model.operator as OperatorKey) || 'equals';
      this.selectedRange =
        (model.range as AgeRangeKey) === undefined
          ? null
          : (model.range as AgeRangeKey);
    } else {
      this.operator = 'equals';
      this.selectedRange = null;
    }
  }

  /* === UI helpers === */

  getOperatorLabel(op: OperatorKey): string {
    const found = this.operatorOptions.find(o => o.value === op);
    return found ? found.label : 'Equals';
  }

  getRangeLabel(range: AgeRangeKey): string {
    if (!range) return 'Select';
    const found = this.rangeOptions.find(r => r.value === range);
    return found ? found.label : 'Select';
  }

  toggleOperatorDropdown(): void {
    this.operatorDropdownOpen = !this.operatorDropdownOpen;
    if (this.operatorDropdownOpen) {
      this.rangeDropdownOpen = false;
    }
  }

  toggleRangeDropdown(): void {
    this.rangeDropdownOpen = !this.rangeDropdownOpen;
    if (this.rangeDropdownOpen) {
      this.operatorDropdownOpen = false;
    }
  }

  selectOperator(op: OperatorKey): void {
    this.operator = op;
    this.operatorDropdownOpen = false;

    if (op === 'blank' || op === 'notBlank') {
      this.selectedRange = null; // range not needed
    }

    this.onFilterChanged();
  }

  selectRange(range: AgeRangeKey): void {
    this.selectedRange = range;
    this.rangeDropdownOpen = false;
    this.onFilterChanged();
  }

  onRangeChange(range: AgeRangeKey): void {
    // not used now, but kept in case template ever uses (ngModelChange)
    this.selectedRange = range;
    this.onFilterChanged();
  }

  onFilterChanged(): void {
    this.params.filterChangedCallback();
  }


  private ageToBucket(age: number): AgeRangeKey {
    if (age < 2) return '0_1';
    if (age > 1 && age < 6) return '2_5';
    if (age > 5 && age < 16) return '6_15';
    return '16_plus';
  }

  /* close dropdowns when clicking outside */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.select-wrapper')) {
      this.operatorDropdownOpen = false;
      this.rangeDropdownOpen = false;
    }
  }
}
